import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { WavyMark } from "@/components/ui/wavy-mark";
import { getPublishedWorkshopReviews, type WorkshopReview } from "@shared/workshop-reviews";
import { DsArrow } from "@/components/sections/ds-icons";

/**
 * TestimonialsMarquee — "Workshop reviews" dual-row infinite auto-scroll.
 *
 * Faithful port of v4 mock (Workshop-Landing-Claude-v4.html, .tm section
 * around lines 2870–2914 + CSS lines 1803–1842 + responsive 2100–2126).
 *
 * Behavior:
 *   - Two rows. Row 1 (.tm-r1) scrolls left over 70s; Row 2 (.tm-r2) scrolls
 *     right over 80s. Each row renders cards twice (originals + aria-hidden
 *     duplicates) and translates -50% so the loop is seamless.
 *   - The tracks run only while the section is on screen (2026-09-09).
 *   - Mobile (<= 600px): marquee disabled, each row becomes a horizontal
 *     snap-scroll strip; aria-hidden duplicates are display:none.
 *   - Per-card avatar circle uses one of 10 fixed background colors
 *     (.tm-av-1 ... .tm-av-10) to match v4 exactly.
 *
 * Only deviation from v4 source: font token strings instead of inline
 * 'Copernicus'/'JetBrains Mono' literals (matches sibling design-workshop
 * sections' convention).
 */

// v4 token mapping (--canvas, --soft, --hairline, --ink, --body, --muted, --primary)
const CANVAS = "#faf9f5";
const SOFT = "#f5f0e8";
const HAIRLINE = "#e6dfd8";
const INK = "#141413";
const BODY_COLOR = "#3d3d3a";
const MUTED = "#6c6a64";
const PRIMARY = "var(--terra)"; // house terra
const NAME_BLUE = "#1F5DB0"; // v4 .tm-name + .tm-foot color
const STAR_GOLD = "#F4B400"; // v4 .tm-stars color

const FONT_DISPLAY = "var(--font-display)";
const FONT_MONO = "var(--font-mono)";
const FONT_SANS = "var(--font-sans)";

// Reviews now sourced from client/src/lib/workshop-reviews.ts — the single
// source of truth shared with the workshop-reviews bento on detail pages.
// The marquee splits the published reviews into two roughly-equal rows for
// the dual-row scroll effect.

function Card({ t, ariaHidden = false }: { t: WorkshopReview; ariaHidden?: boolean }) {
  return (
    <article className="tm-card" {...(ariaHidden ? { "aria-hidden": "true" } : {})}>
      <div className="tm-head-row">
        <div className="tm-av" style={{ background: t.avatarBg }}>{t.initials}</div>
        <div className="tm-meta">
          <div className="tm-name">{t.name}</div>
          <div className="tm-sub">{t.stats ?? ""}</div>
        </div>
      </div>
      <div className="tm-stars">
        ★★★★★{" "}
        <span className="tm-time">
          {t.posted}
          {t.isNew ? <span className="tm-new">New</span> : null}
        </span>
      </div>
      <p className="tm-body">{t.text}</p>
    </article>
  );
}

function Row({ items, modifier }: { items: WorkshopReview[]; modifier: "tm-r1" | "tm-r2" | "tm-r3" }) {
  return (
    <div className={`tm-row ${modifier}`}>
      {items.map((t, i) => (
        <Card key={`${modifier}-${i}`} t={t} />
      ))}
      {items.map((t, i) => (
        <Card key={`${modifier}-dup-${i}`} t={t} ariaHidden />
      ))}
    </div>
  );
}

export function TestimonialsMarquee({
  reviews,
  rows,
  eyebrow = "Workshop reviews",
  headline,
  noBottomBorder = false,
}: {
  /** Override the review pool. Omit to pull design-workshop reviews from the shared source. */
  reviews?: WorkshopReview[];
  /** Explicit per-row assignment ([row1, row2, row3]) — overrides the even auto-split.
   *  Use to group cards by length (e.g. short rows top/bottom, long row in the middle). */
  rows?: WorkshopReview[][];
  /** Pass `null` to drop the eyebrow row entirely. */
  eyebrow?: string | null;
  /** Section headline. Defaults to the ORIGINAL design-page copy
   *  ("Real attendees. Real shipped."). Cowork passes its own copy
   *  explicitly from event-detail.tsx. */
  headline?: ReactNode;
  /** Drop the section's bottom hairline (e.g. when the next section is dark). */
  noBottomBorder?: boolean;
} = {}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  /* 2026-09-09 — three ~5,600px `will-change: transform` tracks were animating
     forever, on every page that mounts this section, whether or not it was
     anywhere near the viewport. One IntersectionObserver parks them while the
     section is off screen. The class is toggled straight on the node rather
     than through state so entering/leaving the viewport does not re-render
     forty-odd cards. `.tm-paused` ships ON, so a page that never runs this
     effect (SSR HTML, no hydration) does not animate either. */
  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.remove("tm-paused");
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => el.classList.toggle("tm-paused", !entry.isIntersecting),
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollByCard = useCallback((dir: 1 | -1) => {
    const el = stripRef.current;
    if (!el) return;
    const card = el.querySelector(".tm-card") as HTMLElement | null;
    const w = (card?.offsetWidth ?? 320) + 14; // card + gap
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  }, []);

  return (
    <section className="tm" id="reviews" data-section="testimonials" style={noBottomBorder ? { borderBottom: "none" } : undefined}>
      <style>{`
        .tm {
          padding: 130px 0 130px;
          background: hsl(48, 20%, 92%);
          overflow: hidden;
          position: relative;
          border-top: 1px solid ${HAIRLINE};
          border-bottom: 1px solid ${HAIRLINE};
        }
        .tm-head {
          padding: 0 56px;
          max-width: 1340px;
          margin: 0 auto 60px;
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 80px;
          align-items: end;
        }
        .tm-eyerow {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 20px;
        }
        .tm-eye {
          font-family: ${FONT_MONO};
          font-size: 14px;
          line-height: 1.2;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          color: ${PRIMARY};
          font-weight: 500;
        }
        .tm-eyeline {
          height: 1px;
          width: 120px;
          background: ${PRIMARY};
          opacity: 0.5;
        }
        .tm-eyestar {
          color: ${PRIMARY};
          width: 20px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .tm-h {
          font-family: ${FONT_DISPLAY};
          font-weight: 500;
          font-variation-settings: 'opsz' 144;
          font-size: clamp(36px, 5vw, 54px);
          line-height: 1.3;
          letter-spacing: -0.06em;
          color: rgb(21, 21, 20);
          max-width: 19ch;
          text-wrap: balance;
          margin: 0;
        }
        .tm-h em {
          font-style: italic;
          color: ${PRIMARY};
        }
        .tm-lede {
          font-family: ${FONT_SANS};
          font-size: 17px;
          line-height: 1.65;
          color: ${BODY_COLOR};
          max-width: 42ch;
          padding-bottom: 14px;
          margin: 0;
        }

        .tm-rows {
          display: flex;
          flex-direction: column;
          gap: 18px;
          position: relative;
          -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%);
                  mask-image: linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%);
        }
        .tm-row {
          display: flex;
          width: max-content;
          gap: 18px;
          /* 2026-09-17, Isaac: "slow down the scrolling speed a bit". Was 96 / 112 / 104s. */
          animation: tmScroll 124s linear infinite;
          will-change: transform;
        }
        .tm-row.tm-r2 {
          animation: tmScrollR 144s linear infinite;
          animation-direction: normal;
        }
        .tm-row.tm-r3 {
          animation: tmScroll 134s linear infinite;
        }
        /* 2026-09-09: a .tm-rows:hover rule used to pause all three tracks. The
           section is ~1,750px tall, so the pointer crossed it constantly and
           every crossing stopped and restarted three long transform
           animations. Off-screen pausing is driven by the viewport observer
           above. 2026-09-17, Isaac: "the review cards should pause on mouse
           hover", so the ROW under the pointer pauses (one track, only while
           the pointer is actually on its cards, not the whole band) and
           :focus-within keeps the same for keyboard users. */
        .tm-rows.tm-paused .tm-row,
        .tm-row:hover,
        .tm-row:focus-within {
          animation-play-state: paused;
        }
        @keyframes tmScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes tmScrollR {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .tm-row {
            animation: none !important;
          }
        }

        .tm-card {
          flex: 0 0 380px;
          background: ${CANVAS};
          border: 1px solid ${HAIRLINE};
          border-radius: 6px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: transform .35s ease, border-color .35s ease;
        }
        .tm-card:hover {
          transform: translateY(-3px);
          border-color: rgba(217,64,0,.35);
        }
        .tm-row > .tm-card:first-child {
          margin-left: 56px;
        }
        .tm-head-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .tm-av {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-family: ${FONT_SANS};
          font-weight: 500;
          font-size: 16px;
          letter-spacing: 0;
          flex-shrink: 0;
        }
        .tm-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          min-width: 0;
        }
        .tm-name {
          font-family: ${FONT_SANS};
          font-size: 17px;
          font-weight: 500;
          color: ${NAME_BLUE};
          line-height: 1.3;
          letter-spacing: 0;
        }
        .tm-sub {
          font-family: ${FONT_SANS};
          font-size: 14px;
          color: ${MUTED};
          line-height: 1.3;
          letter-spacing: 0;
        }
        .tm-stars {
          display: flex;
          align-items: center;
          gap: 8px;
          color: ${STAR_GOLD};
          font-size: 13px;
          letter-spacing: 0.5px;
        }
        .tm-stars .tm-time {
          font-family: ${FONT_SANS};
          font-size: 14px;
          color: ${MUTED};
          letter-spacing: 0;
          font-weight: 400;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .tm-stars .tm-new {
          display: inline-block;
          border: 1px solid ${HAIRLINE};
          font-family: ${FONT_SANS};
          font-size: 12px;
          letter-spacing: 0.3px;
          padding: 2px 7px;
          color: ${INK};
          font-weight: 500;
        }
        .tm-body {
          font-family: ${FONT_SANS};
          font-size: 15px;
          line-height: 1.65;
          color: ${INK};
          margin: 2px 0 0;
          text-wrap: pretty;
          /* Clamp long reviews so one giant testimonial can't stretch a marquee
             card and break the row's uniform height. */
          display: -webkit-box;
          -webkit-line-clamp: 8;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .tm-body em {
          font-style: italic;
          color: ${PRIMARY};
        }
        .tm-foot {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: ${FONT_SANS};
          font-size: 14px;
          color: ${NAME_BLUE};
          font-weight: 400;
          margin-top: auto;
          padding-top: 8px;
          letter-spacing: 0;
        }

        /* Per-avatar background colors — exact v4 values (line 1837–1839) */
        .tm-av-1 { background: #D58A3A; }
        .tm-av-2 { background: #3E73C7; }
        .tm-av-3 { background: #5C8C57; }
        .tm-av-4 { background: #7C4FB3; }
        .tm-av-5 { background: #2E94A3; }
        .tm-av-6 { background: #1F5A7C; }
        .tm-av-7 { background: #A24B7B; }
        .tm-av-8 { background: #3A6E5C; }
        .tm-av-9 { background: #B2562F; }
        .tm-av-10 { background: #4A6E3A; }
        .tm-av-11 { background: #5E7EB8; }

        /* Tablet (<=900px): stack the head */
        @media (max-width: 900px) {
          .tm-head {
            grid-template-columns: 1fr;
            gap: 20px;
            padding: 0 28px;
          }
          .tm {
            padding: 80px 0;
          }
        }

        /* Mobile (<=600px): kill marquee, convert to horizontal snap-scroll */
        /* === Mobile single-row strip + arrow controls (hidden on desktop) === */
        .tm-mobile-strip,
        .tm-mobile-controls { display: none; }

        @media (max-width: 600px) {
          .tm {
            padding: 70px 0;
          }
          .tm-head {
            margin: 0 auto 36px;
          }
          /* Hide the desktop two-row marquee */
          .tm-rows { display: none; }

          /* Show the single mobile strip. Default flex stretch keeps every
             card the same height (per Isaac 2026-07-02). */
          .tm-mobile-strip {
            display: flex;
            gap: 14px;
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            /* Left inset matches the standard mobile container (px-6 = 24px). */
            padding: 0 24px 12px;
            scrollbar-width: none;
          }
          .tm-mobile-strip::-webkit-scrollbar { display: none; }
          .tm-mobile-strip .tm-card {
            flex: 0 0 88vw;
            max-width: 320px;
            scroll-snap-align: start;
            padding: 18px;
            gap: 10px;
          }
          .tm-mobile-strip .tm-body {
            font-size: 14px;
            line-height: 1.6;
          }
          .tm-mobile-strip .tm-av {
            width: 32px;
            height: 32px;
            font-size: 13px;
          }
          .tm-mobile-strip .tm-name {
            font-size: 15px;
          }
          .tm-mobile-strip .tm-sub,
          .tm-mobile-strip .tm-stars .tm-time {
            font-size: 13px;
          }
          .tm-mobile-strip .tm-stars {
            font-size: 12px;
          }
          .tm-mobile-strip .tm-foot {
            font-size: 13px;
          }

          /* Arrow controls — elegant hairline circular buttons */
          .tm-mobile-controls {
            display: flex;
            justify-content: center;
            gap: 14px;
            margin-top: 22px;
            padding: 0 20px;
          }
          .tm-arrow {
            width: 44px;
            height: 44px;
            border-radius: 9999px;
            border: 1px solid ${HAIRLINE};
            background: transparent;
            color: ${INK};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;
            transition: background 200ms ease, border-color 200ms ease, color 200ms ease;
          }
          .tm-arrow:hover,
          .tm-arrow:focus-visible {
            background: ${INK};
            border-color: ${INK};
            color: ${CANVAS};
            outline: none;
          }
          .tm-arrow:active {
            transform: scale(0.96);
          }
        }
      `}</style>

      <div className="tm-head">
        <div>
          {eyebrow !== null && (
            <div className="tm-eyerow">
              <span className="tm-eye">{eyebrow}</span>
              <span className="tm-eyeline" aria-hidden="true" />
              <span className="tm-eyestar" aria-hidden="true">
                <WavyMark size={18} color={PRIMARY} />
              </span>
            </div>
          )}
          <h2 className="tm-h">
            {headline ?? (
              <>
                Real attendees. Real <em>shipped</em>.
              </>
            )}
          </h2>
        </div>
      </div>

      {(() => {
        // Pull from the shared reviews source. Scope to the design workshop
        // so design-specific reviews (Lena, Caleb) appear here; the general
        // pool of cross-workshop reviews comes through too.
        const all = reviews ?? rows?.flat() ?? getPublishedWorkshopReviews("claude-for-design-workshop");
        // Explicit rows (grouped by length, e.g. cowork's tri-row) render as
        // given; the default is the design page's ORIGINAL dual-row split so
        // the two-track scroll doesn't bunch the same reviewer on both rows.
        const mid = Math.ceil(all.length / 2);
        const splitRows = rows ?? [all.slice(0, mid), all.slice(mid)];
        const modifiers = ["tm-r1", "tm-r2", "tm-r3"] as const;
        return (
          <>
            <div className="tm-rows tm-paused" ref={rowsRef}>
              {splitRows.slice(0, 3).map((items, i) =>
                items.length > 0 ? <Row key={modifiers[i]} items={items} modifier={modifiers[i]} /> : null,
              )}
            </div>

            {/* Mobile-only single row with arrow controls. Hidden on desktop. */}
            <div className="tm-mobile-strip" ref={stripRef}>
              {all.map((t, i) => (
                <Card key={`mobile-${i}`} t={t} />
              ))}
            </div>
          </>
        );
      })()}
      <div className="tm-mobile-controls" aria-hidden={false}>
        <button
          type="button"
          className="tm-arrow"
          aria-label="Previous testimonial"
          onClick={() => scrollByCard(-1)}
        >
          {/* The system's drawn arrow, not a geometric chevron. It carries its
              own stroke in currentColor, so it reads on paper and on a dark
              room alike. */}
          <DsArrow direction="left" size={24} />
        </button>
        <button
          type="button"
          className="tm-arrow"
          aria-label="Next testimonial"
          onClick={() => scrollByCard(1)}
        >
          <DsArrow direction="right" size={24} />
        </button>
      </div>
    </section>
  );
}
